// screens/People.tsx — B17c: the people. A person here is a DECISION —
// mail senders deliberately do not auto-create parties (an identity is not
// a person; resolution belongs to Huan), so this list starts honest-empty
// and grows one deliberate act at a time.
//
// Parked sits under a quiet fold: parked is a real state, not a deletion.
// Identity collisions render as the AttentionTile — the same trust
// primitive Today uses, because "this address changed hands" is the same
// class of fact wherever it appears. Create is files-are-approval: a named
// person is real immediately; the optional address rides along.
import { useEffect, useState } from 'preact/hooks';
import { AttentionTile } from '@vowric/ui';
import {
  ApiError,
  claimPartyIdentity,
  createParty,
  listParties,
  listPartyCollisions,
  type PartyApi,
  type PartyCollisionApi,
} from '../lib/api';
import './People.css';

export interface PeopleProps {
  tenantId: string;
  onOpen: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready' };

function sinceLabel(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function People({ tenantId, onOpen }: PeopleProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [parties, setParties] = useState<PartyApi[]>([]);
  const [collisions, setCollisions] = useState<PartyCollisionApi[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [parkedOpen, setParkedOpen] = useState(false);

  function refresh() {
    return listParties(tenantId, { includeParked: true })
      .then((rows) => { setParties(rows); setLoad({ phase: 'ready' }); })
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message:
            err instanceof ApiError
              ? err.message
              : 'the server did not answer — your connection dropped or it is down',
        });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // Collisions are the warning layer; a failed read costs the tiles,
    // never the list.
    void listPartyCollisions(tenantId)
      .then(setCollisions)
      .catch(() => { /* list renders without tiles */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setNote(null);
    setBusy(true);
    try {
      const party = await createParty(tenantId, { kind: 'person', displayName: trimmed });
      const address = email.trim();
      if (address) {
        // The takeover answer is never swallowed (§2.1): if this address
        // moved here from someone else, say so where it happened.
        const claimed = await claimPartyIdentity(tenantId, party.id, { kind: 'email', value: address });
        if (claimed.displaced) {
          setNote('That address changed hands — the previous holder shows above.');
          void listPartyCollisions(tenantId)
            .then(setCollisions)
            .catch(() => { /* the warning tiles refresh next visit */ });
        }
      }
      setName('');
      setEmail('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not added');
    } finally {
      setBusy(false);
    }
  }

  const active = parties.filter((p) => !p.parkedAt);
  const parked = parties.filter((p) => p.parkedAt);

  function Row({ p }: { p: PartyApi }) {
    return (
      <button type="button" class="people__row" onClick={() => onOpen(p.id)}>
        <span class="people__row-main">
          <span class="people__row-name">{p.displayName}</span>
          {p.kind === 'organization' ? <span class="people__row-kind">org</span> : null}
        </span>
        <span class="people__row-meta">
          <span class="people__row-since">{sinceLabel(p.firstTouch.at ?? p.createdAt)}</span>
        </span>
      </button>
    );
  }

  return (
    <div class="people">
      <div class="people__eyebrow">People</div>

      <div class="people__add">
        <input
          class="people__add-input"
          type="text"
          placeholder="Add a person…"
          value={name}
          disabled={busy}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
        />
        <input
          class="people__add-input people__add-input--email"
          type="email"
          placeholder="email (optional)"
          value={email}
          disabled={busy}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
        />
        <button type="button" class="people__act people__act--primary" disabled={busy || !name.trim()} onClick={() => void add()}>
          Add
        </button>
      </div>
      {note ? <div class="people__note">{note}</div> : null}

      {collisions.length > 0 ? (
        <section class="people__collisions">
          {collisions.map((c) => (
            <AttentionTile
              key={`${c.kind}:${c.value}`}
              severity="warning"
              icon="⇄"
              title={`${c.kind}: ${c.value}`}
              sub={`${c.previousHolder.displayName ?? '?'} → ${c.currentHolder.displayName ?? '?'}`}
            />
          ))}
        </section>
      ) : null}

      {load.phase === 'loading' ? (
        <div class="people__skeleton" aria-hidden="true">
          <div class="people__skeleton-row" />
          <div class="people__skeleton-row" />
        </div>
      ) : load.phase === 'error' ? (
        <div class="people__say">
          The people could not be read.
          <br />
          <span class="people__say-soft">{(load as { message: string }).message}</span>
        </div>
      ) : (
        <>
          {active.length === 0 ? (
            <div class="people__say">
              No people yet.
              <br />
              <span class="people__say-soft">
                A person here is a decision — mail senders never become people on
                their own. Name one above; threads and promises gather to them.
              </span>
            </div>
          ) : (
            <div class="people__list">
              {active.map((p) => <Row p={p} key={p.id} />)}
            </div>
          )}

          {parked.length > 0 ? (
            <section class="people__group people__group--parked">
              <button
                type="button"
                class="people__fold"
                aria-expanded={parkedOpen}
                onClick={() => setParkedOpen(!parkedOpen)}
              >
                Parked · {parked.length}
              </button>
              {parkedOpen ? parked.map((p) => <Row p={p} key={p.id} />) : null}
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
